import { useEffect, useState } from 'react';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Pure relative-time formatter. `now` is injectable so callers (and tests) get
 * deterministic output. Future timestamps (clock skew) collapse to "just now".
 */
export function formatRelativeTime(
  input: string | number | Date,
  now: number = Date.now(),
): string {
  const ts = input instanceof Date ? input.getTime() : new Date(input).getTime();
  if (Number.isNaN(ts)) return 'Unknown time';

  const elapsed = now - ts;
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m ago`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h ago`;
  return `${Math.floor(elapsed / DAY)}d ago`;
}

/**
 * Relative timestamp that stays current. Unlike a one-shot formatter, this
 * re-renders every `intervalMs` so "just now" advances to "1m ago" on its own,
 * without waiting for an unrelated re-render. The interval is cleared on unmount.
 */
export function useRelativeTime(
  input: string | number | Date,
  intervalMs = 30_000,
): string {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return formatRelativeTime(input, now);
}
